<template>
    <div class="referenzen-area ptb-100">
        <div class="container">
            <div class="section-title" data-aos="fade-up">
                <span class="sub-title">Referenzen</span>
                <h2>Unsere öffentlichen Projekte</h2>
                <p>Überzeugen Sie sich selbst von der Qualität unserer Arbeit</p>
            </div>

            <!-- Category Filter -->
            <div class="category-filter" data-aos="fade-up" data-aos-delay="100">
                <button
                    v-for="category in categories"
                    :key="category.id"
                    :class="['filter-btn', { active: activeCategory === category.id }]"
                    @click="activeCategory = category.id"
                >
                    <i :class="category.icon"></i>
                    {{ category.name }}
                </button>
            </div>

            <div class="row">
                <div
                    class="col-lg-4 col-md-6"
                    v-for="(project, index) in filteredProjects"
                    :key="project.id"
                    data-aos="fade-up"
                    :data-aos-delay="200 + (index * 100)"
                >
                    <div class="project-card">
                        <div class="project-image">
                            <!-- Card Slider -->
                            <Carousel
                                :items-to-show="1"
                                :wrap-around="true"
                                class="card-slider"
                            >
                                <Slide v-for="(screenshot, index) in project.screenshots" :key="index">
                                    <img
                                        :src="screenshot"
                                        :alt="project.title + ' - Bild ' + (index + 1)"
                                        @click="openLightbox(project, index)"
                                    />
                                </Slide>
                                <template #addons>
                                    <Navigation v-if="project.screenshots.length > 1" />
                                </template>
                            </Carousel>
                            <div class="project-overlay" @click="openLightbox(project, 0)">
                                <i class="bx bx-zoom-in"></i>
                            </div>
                            <div class="category-badge">
                                {{ getCategoryName(project.category) }}
                            </div>
                            <div v-if="project.screenshots.length > 1" class="image-count">
                                <i class="bx bx-images"></i> {{ project.screenshots.length }}
                            </div>
                        </div>
                        <div class="project-info" @click="openLightbox(project, 0)">
                            <h4>{{ project.title }}</h4>
                            <p>{{ project.description }}</p>
                            <div class="project-tags">
                                <span v-for="tag in project.technologies" :key="tag">{{ tag }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No Projects Message -->
            <div v-if="filteredProjects.length === 0" class="no-projects">
                <i class="bx bx-folder-open"></i>
                <p>Noch keine Projekte in dieser Kategorie</p>
            </div>
        </div>

        <!-- Lightbox -->
        <div class="lightbox" v-if="lightboxOpen" @click="closeLightbox">
            <div class="lightbox-content" @click.stop>
                <button class="lightbox-close" @click="closeLightbox">
                    <i class="bx bx-x"></i>
                </button>

                <!-- Lightbox Slider -->
                <div class="lightbox-slider">
                    <Carousel
                        :items-to-show="1"
                        :wrap-around="true"
                        v-model="lightboxSlide"
                        ref="lightboxCarousel"
                    >
                        <Slide v-for="(screenshot, index) in activeProject.screenshots" :key="index">
                            <img :src="screenshot" :alt="activeProject.title + ' - Bild ' + (index + 1)" />
                        </Slide>
                        <template #addons>
                            <Navigation />
                            <Pagination />
                        </template>
                    </Carousel>
                </div>

                <div class="lightbox-info">
                    <h3>{{ activeProject.title }}</h3>
                    <p>{{ activeProject.description }}</p>
                    <div class="project-tags">
                        <span v-for="tag in activeProject.technologies" :key="tag">{{ tag }}</span>
                    </div>
                    <div v-if="activeProject.url" class="project-url">
                        <a :href="activeProject.url" target="_blank" rel="noopener noreferrer">
                            <i class="bx bx-link-external"></i> {{ activeProject.url }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Carousel, Slide, Navigation, Pagination } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';

export default {
    name: 'Referenzen',
    components: {
        Carousel,
        Slide,
        Navigation,
        Pagination
    },
    data() {
        return {
            activeCategory: 'all',
            lightboxOpen: false,
            lightboxSlide: 0,
            activeProject: {},
            categories: [
                { id: 'all', name: 'Alle', icon: 'bx bx-grid-alt' },
                { id: 'landingpages', name: 'Landingpages', icon: 'bx bx-layout' },
                { id: 'webshops', name: 'Webshops', icon: 'bx bx-cart' },
                { id: 'mobile-apps', name: 'Mobile Apps', icon: 'bx bx-mobile-alt' },
                { id: 'complex', name: 'Komplexe Projekte', icon: 'bx bx-code-block' },
                { id: 'coming-soon', name: 'Demnächst', icon: 'bx bx-time-five' },
            ],
            projects: [
                // Landingpages
                {
                    id: 1,
                    category: 'landingpages',
                    screenshots: [
                        require("../../assets/img/projects/project1.png")
                    ],
                    title: "FutureOceanKids",
                    description: "Landingpage für eine erfolgreiche NGO.",
                    technologies: ["Vue.js", "SCSS", "Animation"],
                    url: "https://futureoceankids.org"
                },
                // Mobile Apps
                // {
                //     id: 2,
                //     category: 'mobile-apps',
                //     screenshots: [
                //         require("../../assets/img/projects/login_screen_2.png")
                //     ],
                //     title: "Zeiterfassungs-App für interne Firmennutzung",
                //     description: "Mobile App zur Zeiterfassung und Projektmanagement mit Offline-Funktionalität und Synchronisation.",
                //     technologies: ["Flutter", "Dart", "Supabase", "Firebase"],
                //     url: 'Aus Datenschutzgründen nicht öffentlich zugänglich'
                // },
                // {
                //     id: 3,
                //     category: 'mobile-apps',
                //     screenshots: [
                //         require("../../assets/img/projects/fake_app_1.jpg")
                //     ],
                //     title: "DiaryPlus App",
                //     description: "Mobile App zur Ernährungs- und Fitnessverfolgung mit Barcode-Scanner und personalisierten Empfehlungen für einen spezifischen Kunden",
                //     technologies: ["Flutter", "Dart", "Supabase", "Firebase"],
                //     url: 'Aus Datenschutzgründen nicht öffentlich zugänglich'
                // },
                // Complex Projects
                // {
                //     id: 4,
                //     category: 'complex',
                //     screenshots: [
                //         require("../../assets/img/projects/finanz_ein_ausgaben.png"),
                //         require("../../assets/img/projects/finanz_cashflow.png"),
                //         require("../../assets/img/projects/project6_1.png"),
                //         require("../../assets/img/projects/project67_2.png"),
                //         require("../../assets/img/projects/project67_3.png"),
                //     ],
                //     title: "Finanzplanungs-Portal für ein IT-Unternehmen",
                //     description: "Umfangreiches Finanz-Portal mit über 50 Features wie Personalakte, optionale Abteilungsverteilung und Benutzer-Rollen mit individueller Rechtevergabe sowie der Möglichkeit, einen Mitarbeiter-Zugang zu gewähren.",
                //     technologies: ["Vue.js", "Nuxt.js", "PostgreSQL", "Golang"],
                //     url: 'Aus Datenschutzgründen nicht öffentlich zugänglich'
                // },
                // {
                //     id: 5,
                //     category: 'complex',
                //     screenshots: [
                //         require("../../assets/img/projects/project7.png"),
                //         require("../../assets/img/projects/project7_1.png"),
                //         require("../../assets/img/projects/project67_2.png"),
                //         require("../../assets/img/projects/project67_3.png"),
                //     ],
                //     title: "Auslastungsplanung-Tool für ein IT-Unternehmen",
                //     description: "Komplexes Planungstool zur Ressourcen- und Auslastungsoptimierung mit Kalenderintegration und Drag-and-Drop-Funktionalität. Ebenso eine Personalakte, optionale Abteilungsverteilung und Benutzer-Rollen mit individueller Rechtevergabe sowie der Möglichkeit, einen Mitarbeiter-Zugang zu gewähren.",
                //     technologies: ["Vue.js", "Nuxt.js", "PostgreSQL", "Golang"],
                //     url: 'Aus Datenschutzgründen nicht öffentlich zugänglich'
                // },
                // {
                //     id: 3,
                //     category: 'complex',
                //     screenshots: [
                //         require("../../assets/img/projects/raspberry.jpg"),
                //     ],
                //     title: "Raspberry Pi Integration an Zeiterfassungssystem für ein IT-Unternehmen",
                //     description: "Integration von Raspberry Pi Geräten zur Zeiterfassung mittels RFID-Technologie, inklusive Backend-Entwicklung zur Verwaltung und Auswertung der erfassten Daten.",
                //     technologies: ["Bootstrap", "NeDB", "Express", "Node.js"],
                //     url: 'Aus Datenschutzgründen nicht öffentlich zugänglich'
                // },
                {
                    id: 2,
                    category: 'coming-soon',
                    screenshots: [
                    ],
                    title: "Diese Sektion wird bald erweitert",
                    description: "",
                    technologies: [],
                    url: ''
                }
            ]
        }
    },
    computed: {
        filteredProjects() {
            if (this.activeCategory === 'all') {
                return this.projects;
            }
            return this.projects.filter(p => p.category === this.activeCategory);
        }
    },
    methods: {
        getCategoryName(categoryId) {
            const category = this.categories.find(c => c.id === categoryId);
            return category ? category.name : categoryId;
        },
        openLightbox(project, startIndex = 0) {
            this.activeProject = project;
            this.lightboxSlide = startIndex;
            this.lightboxOpen = true;
            document.body.style.overflow = 'hidden';
        },
        closeLightbox() {
            this.lightboxOpen = false;
            document.body.style.overflow = '';
        }
    }
}
</script>

<style scoped>
.referenzen-area {
    background-color: #f8f9fa;
}

/* Category Filter */
.category-filter {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-bottom: 40px;
}

.filter-btn {
    background: #ffffff;
    border: 2px solid #e0e0e0;
    border-radius: 30px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    color: #4a6f8a;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-btn i {
    font-size: 18px;
}

.filter-btn:hover {
    border-color: #010215;
    color: #010215;
}

.filter-btn.active {
    background: #010215;
    border-color: #010215;
    color: #ffffff;
}

/* Project Card */
.project-card {
    background: #ffffff;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 30px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
}

.project-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.project-image {
    position: relative;
    overflow: hidden;
    height: 220px;
}

.project-image img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    cursor: pointer;
    border-radius: 0;
}

/* Card Slider */
.card-slider {
    height: 220px;
}

.card-slider :deep(.carousel__viewport) {
    height: 220px;
}

.card-slider :deep(.carousel__track) {
    height: 220px;
}

.card-slider :deep(.carousel__slide) {
    height: 220px;
}

.card-slider :deep(.carousel__prev),
.card-slider :deep(.carousel__next) {
    background: rgba(1, 2, 21, 0.8);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    color: #fff;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.project-card:hover .card-slider :deep(.carousel__prev),
.project-card:hover .card-slider :deep(.carousel__next) {
    opacity: 1;
}

.card-slider :deep(.carousel__prev:hover),
.card-slider :deep(.carousel__next:hover) {
    background: #010215;
}

.project-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(1, 2, 21, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
    pointer-events: none;
}

.project-card:hover .project-overlay {
    opacity: 1;
    pointer-events: auto;
}

.project-overlay i {
    font-size: 50px;
    color: #ffffff;
}

.category-badge {
    position: absolute;
    top: 15px;
    left: 15px;
    background: linear-gradient(135deg, #010215 0%, #010215 100%);
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 2;
}

.image-count {
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    padding: 5px 10px;
    border-radius: 15px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 5px;
}

.image-count i {
    font-size: 14px;
}

.project-info {
    padding: 20px;
    cursor: pointer;
}

.project-info h4 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #080a3c;
}

.project-info p {
    font-size: 14px;
    color: #4a6f8a;
    margin-bottom: 15px;
    line-height: 1.6;
}

.project-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.project-tags span {
    background: #e8e8e8;
    color: #010215;
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 15px;
    font-weight: 500;
}

/* No Projects */
.no-projects {
    text-align: center;
    padding: 60px 20px;
    color: #4a6f8a;
}

.no-projects i {
    font-size: 60px;
    opacity: 0.3;
    margin-bottom: 15px;
    display: block;
}

.no-projects p {
    font-size: 16px;
}

/* Lightbox */
.lightbox {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.lightbox-content {
    max-width: 1000px;
    width: 100%;
    background: #ffffff;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
}

.lightbox-close {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #010215;
    border: none;
    color: #ffffff;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease;
}

.lightbox-close:hover {
    background: #000000;
}

.lightbox-close i {
    font-size: 24px;
}

/* Lightbox Slider */
.lightbox-slider {
    background: #000;
}

.lightbox-slider img {
    width: 100%;
    max-height: 60vh;
    object-fit: contain;
    border-radius: 0;
}

.lightbox-slider :deep(.carousel__prev),
.lightbox-slider :deep(.carousel__next) {
    background: rgba(1, 2, 21, 0.9);
    border-radius: 50%;
    width: 45px;
    height: 45px;
    color: #fff;
}

.lightbox-slider :deep(.carousel__prev:hover),
.lightbox-slider :deep(.carousel__next:hover) {
    background: #010215;
}

.lightbox-slider :deep(.carousel__pagination) {
    padding: 10px 0;
    background: rgba(0, 0, 0, 0.5);
}

.lightbox-slider :deep(.carousel__pagination-button) {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
}

.lightbox-slider :deep(.carousel__pagination-button--active) {
    background: #010215;
}

.lightbox-info {
    padding: 25px;
}

.lightbox-info h3 {
    font-size: 24px;
    margin-bottom: 10px;
    color: #080a3c;
}

.lightbox-info p {
    color: #4a6f8a;
    margin-bottom: 15px;
}

.project-url {
    margin-top: 15px;
}

.project-url a {
    color: #010215;
    text-decoration: none;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: color 0.3s ease;
}

.project-url a:hover {
    color: #000000;
}

.project-url i {
    font-size: 18px;
}

/* Responsive */
@media (max-width: 768px) {
    .category-filter {
        gap: 8px;
    }

    .filter-btn {
        padding: 8px 15px;
        font-size: 13px;
    }

    .filter-btn i {
        display: none;
    }

    .project-image {
        height: 180px;
    }

    .project-image img {
        height: 180px;
    }

    .card-slider,
    .card-slider :deep(.carousel__viewport),
    .card-slider :deep(.carousel__track),
    .card-slider :deep(.carousel__slide) {
        height: 180px;
    }

    .lightbox-slider img {
        max-height: 40vh;
    }

    .lightbox-slider :deep(.carousel__prev),
    .lightbox-slider :deep(.carousel__next) {
        width: 35px;
        height: 35px;
    }
}
</style>
